import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import {
  LandingPageLayers,
  DesignSystem,
  Plugin,
  ThemeItem,
} from "@/types/ThemeTypes";
import { defaultLandingConfig } from "@/config/defaultLandingConfig";
import { API_URL } from "@/config";
import { getTenantContext } from "@/utils/tenant";

interface TenantFeConfigState {
  tenantId?: string;
  tenantDomain?: string;
  landingPage: LandingPageLayers | null;
  designSystem: DesignSystem;
  plugins: Plugin[];
  isLoading: boolean;
  apiVersion: string;
}

interface TenantFeConfigResponse {
  apiVersion?: string;
  // Workspace/tenant logo, surfaced from TenantResponseDto.logo on the BE.
  logo?: string;
  coreTheme?: {
    themeSelected?: string;
    themeList?: ThemeItem[];
  };
}

// Token identifying the generic placeholder logo shipped in the default theme.
const PLACEHOLDER_LOGO_TOKEN = "logo-preview-site-placeholder";

/**
 * Mirror of the studio's patchLogoPlaceholderInConfig (SAAS-2388): when a header
 * block still carries the placeholder logo and the workspace has a real logo,
 * fall back to the workspace logo so the live site matches the studio preview.
 * Placeholder-only — user-customised logos (not containing the token) are kept.
 */
const applyWorkspaceLogoToHeader = (
  landingPage: LandingPageLayers,
  workspaceLogo?: string
): LandingPageLayers => {
  if (!workspaceLogo || !Array.isArray(landingPage.header)) {
    return landingPage;
  }

  let changed = false;
  const header = landingPage.header.map((block) => {
    const props = block.properties as { logoImage?: unknown } | undefined;
    const logoImage = props?.logoImage;
    if (
      typeof logoImage === "string" &&
      logoImage.includes(PLACEHOLDER_LOGO_TOKEN)
    ) {
      changed = true;
      return {
        ...block,
        properties: { ...props, logoImage: workspaceLogo },
      };
    }
    return block;
  });

  return changed ? { ...landingPage, header } : landingPage;
};

const TenantFeConfigContext = createContext<TenantFeConfigState | undefined>(
  undefined
);

export const TenantFeConfigProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // State for landing page config (initialize with default)
  const [landingPage, setLandingPage] = useState<LandingPageLayers | null>(
    defaultLandingConfig.landingPage
  );
  const [designSystem, setDesignSystem] = useState<DesignSystem>(
    defaultLandingConfig.designSystem
  );
  const [plugins, setPlugins] = useState<Plugin[]>(
    defaultLandingConfig.plugins
  );
  const [isLoading, setIsLoading] = useState(true);
  const [apiVersion, setApiVersion] = useState<string>("v1");

  const [tenantId, setTenantId] = useState<string | undefined>(undefined);
  const [tenantDomain, setTenantDomain] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    const fetchConfig = async () => {
      const { tenantId: tId, tenantDomain: tDomain } = getTenantContext();
      setTenantId(tId);
      setTenantDomain(tDomain);

      // If no tenantId or tenantDomain, use default config
      if (!tId || !tDomain) {
        setIsLoading(false);
        return;
      }

      try {
        // Step 1: Fetch tenant FE config từ API
        const endpoint = `${API_URL}/super-admin/tenant/client/${tId}/fe-config-env`;

        const response = await fetch(endpoint, {
          headers: {
            "x-tenant-domain": tDomain,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const tenantFeConfig: TenantFeConfigResponse = await response.json();

        if (!tenantFeConfig) {
          throw new Error("Failed to fetch config");
        }

        // Step 2: Extract theme config (giống pattern từ tenant-end-user/landing)
        const { coreTheme } = tenantFeConfig;
        const themeList = Array.isArray(coreTheme?.themeList)
          ? coreTheme?.themeList
          : [];

        const selectedTheme =
          themeList?.find(
            (t: ThemeItem) => t.key === coreTheme?.themeSelected
          ) || null;

        // Step 3: Check if config is null, fallback to defaultLandingConfig
        const config = selectedTheme?.config;
        if (!config) {
          // No saved theme config: still apply the workspace-logo fallback to the
          // default landing page so the header shows the workspace logo instead of
          // the generic placeholder.
          setLandingPage(
            applyWorkspaceLogoToHeader(
              defaultLandingConfig.landingPage,
              tenantFeConfig.logo
            )
          );
          setIsLoading(false);
          return;
        }

        // Step 4: Extract data from config
        const { layers, designSystem: ds } = config;
        const landingPageData = layers?.landingPage || null;
        const pluginsData = selectedTheme?.plugins || [];

        // Step 5: Set state (only set if there is data from API)
        if (landingPageData) {
          setLandingPage(
            applyWorkspaceLogoToHeader(landingPageData, tenantFeConfig.logo)
          );
        }
        if (ds) {
          setDesignSystem(ds);
        }
        if (pluginsData.length > 0) {
          setPlugins(pluginsData);
        }

        // Set API version if there is one
        if (tenantFeConfig.apiVersion) {
          setApiVersion(tenantFeConfig.apiVersion);
        }
      } catch (error) {
        console.error("Error fetching tenant FE config:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const value = useMemo(
    () => ({
      tenantId,
      tenantDomain,
      landingPage,
      designSystem,
      plugins,
      isLoading,
      apiVersion,
    }),
    [
      tenantId,
      tenantDomain,
      landingPage,
      designSystem,
      plugins,
      isLoading,
      apiVersion,
    ]
  );

  return (
    <TenantFeConfigContext.Provider value={value}>
      {children}
    </TenantFeConfigContext.Provider>
  );
};

export const useTenantFeConfig = (): TenantFeConfigState => {
  const context = useContext(TenantFeConfigContext);
  if (context === undefined) {
    throw new Error(
      "useTenantFeConfig must be used within a TenantFeConfigProvider"
    );
  }
  return context;
};
